import Phaser from 'phaser';
import { AIDifficulty, GameEvents, GameMode, IPlayer, IRoundResult, ITile } from '../core/types';
import { EventBus } from '../utils/EventBus';
import { GameManager } from '../core/GameManager';
import { DominoBoard } from '../objects/DominoBoard';
import { PlayerHand } from '../objects/PlayerHand';
import { OpponentHand } from '../objects/OpponentHand';
import { GameHUD } from '../ui/GameHUD';
import { Toast } from '../ui/Toast';
import { COLORS } from '../utils/Constants';

export class GameScene extends Phaser.Scene {
  private difficulty!: AIDifficulty;
  private gameManager!: GameManager;
  private board!: DominoBoard;
  private playerHand!: PlayerHand;
  private opponentHand!: OpponentHand;
  private hud!: GameHUD;
  private humanPlayerId = 'player-1';
  private aiPlayerId = 'player-2';

  constructor() {
    super('GameScene');
  }

  init(data: { difficulty?: AIDifficulty }) {
    this.difficulty = data?.difficulty || AIDifficulty.Easy;
  }

  create() {
    const w = this.cameras.main.width;
    const h = this.cameras.main.height;

    // ── Background ──
    const bg = this.add.graphics();
    bg.fillGradientStyle(COLORS.CASINO_FELT, COLORS.CASINO_FELT, COLORS.CASINO_DARK, COLORS.CASINO_DARK, 1);
    bg.fillRect(0, 0, w, h);

    // Subtle felt texture dots
    const dots = this.add.graphics();
    dots.fillStyle(0xffffff, 0.015);
    for (let x = 0; x < w; x += 20) {
      for (let y = 0; y < h; y += 20) {
        dots.fillCircle(x + Math.random() * 10, y + Math.random() * 10, 1);
      }
    }

    // ── Game Objects ──
    this.board = new DominoBoard(this, w / 2, h / 2 - 20, w * 0.85, 160);
    this.playerHand = new PlayerHand(this, w / 2, h - 80);
    this.opponentHand = new OpponentHand(this, w / 2, 90);
    this.hud = new GameHUD(this);

    // ── Difficulty label ──
    const diffLabels: Record<string, string> = {
      [AIDifficulty.Easy]: '🟢 Fácil',
      [AIDifficulty.Medium]: '🟡 Medio',
      [AIDifficulty.Hard]: '🔴 Difícil',
    };
    this.add.text(w - 10, h - 10, `IA: ${diffLabels[this.difficulty] || 'Fácil'}`, {
      fontSize: '14px',
      color: '#8faa9a',
    }).setOrigin(1, 1);

    // ── Back button ──
    const backBtn = this.add.text(10, 10, '← Menú', {
      fontSize: '16px',
      color: '#d4af37',
      backgroundColor: '#0005',
      padding: { x: 10, y: 6 },
    }).setInteractive({ useHandCursor: true });
    backBtn.on('pointerdown', () => {
      this.cleanupEvents();
      this.scene.start('MenuScene');
    });

    // ── Setup Events ──
    this.setupEvents();

    // ── Start Game ──
    this.startGame();
  }

  private startGame() {
    this.gameManager = new GameManager();

    const players: IPlayer[] = [
      {
        id: this.humanPlayerId,
        name: 'Jugador',
        type: 'human',
        hand: [],
        avatar: '😎',
      },
      {
        id: this.aiPlayerId,
        name: 'IA',
        type: 'ai',
        hand: [],
        avatar: '🤖',
        difficulty: this.difficulty,
      },
    ];

    this.gameManager.startNewGame(GameMode.VsAI, this.difficulty, players);

    // Get dealt hands
    const humanPlayer = players[0];
    const aiPlayer = players[1];

    // Show tiles
    this.playerHand.setTiles(humanPlayer.hand);
    this.opponentHand.dealAnimation(aiPlayer.hand.length, 80);

    // Highlight playable tiles after deal animation
    this.time.delayedCall(humanPlayer.hand.length * 80 + 400, () => {
      this.updateHandHighlights();
      this.hud.updateTurn('Jugador', true);
      Toast.show(this, '🎲 ¡Partida iniciada! Toca una ficha verde');
    });
  }

  private setupEvents() {
    EventBus.on(GameEvents.TilePlayed, this.onTilePlayed, this);
    EventBus.on(GameEvents.TileInvalid, this.onTileInvalid, this);
    EventBus.on(GameEvents.PlayerPassed, this.onPlayerPassed, this);
    EventBus.on(GameEvents.RoundEnd, this.onRoundEnd, this);
    EventBus.on(GameEvents.TilePlayRequested, this.onTilePlayRequested, this);
  }

  private cleanupEvents() {
    EventBus.off(GameEvents.TilePlayed, this.onTilePlayed, this);
    EventBus.off(GameEvents.TileInvalid, this.onTileInvalid, this);
    EventBus.off(GameEvents.PlayerPassed, this.onPlayerPassed, this);
    EventBus.off(GameEvents.RoundEnd, this.onRoundEnd, this);
    EventBus.off(GameEvents.TilePlayRequested, this.onTilePlayRequested, this);
  }

  // ── When the player clicks a tile in their hand ──
  private onTilePlayRequested(tile: ITile) {
    const success = this.gameManager.playTile(this.humanPlayerId, tile);
    if (!success) {
      // tile might be invalid or not player's turn
    }
  }

  // ── When any tile is played (human or AI) ──
  private onTilePlayed(data: { playerId: string; tile: ITile; end: string; boardTile: any }) {
    const { playerId, end, boardTile } = data;

    // Add tile to visual board
    this.board.addTile(boardTile, end as any, true);

    if (playerId === this.humanPlayerId) {
      // Remove from player hand visually
      this.playerHand.removeTile(data.tile);
      this.playerHand.disableInteraction();

      // After AI plays, re-enable
      this.time.delayedCall(1200, () => {
        this.updateHandHighlights();
      });
    } else {
      // AI played
      this.opponentHand.removeOne(true);
      Toast.show(this, '🤖 IA jugó una ficha');

      // Re-enable player interaction
      this.time.delayedCall(600, () => {
        this.updateHandHighlights();
        this.hud.updateTurn('Jugador', true);
      });
    }
  }

  private onTileInvalid() {
    Toast.show(this, '❌ Esa ficha no se puede jugar aquí');
  }

  private onPlayerPassed(data: { playerId: string }) {
    if (data.playerId === this.humanPlayerId) {
      Toast.show(this, '⏭ Pasaste turno');
    } else {
      Toast.show(this, '🤖 IA no puede jugar — pasó turno');
      this.time.delayedCall(600, () => {
        this.updateHandHighlights();
        this.hud.updateTurn('Jugador', true);
      });
    }
  }

  private onRoundEnd(result: IRoundResult) {
    this.playerHand.disableInteraction();

    const isWin = result.winnerId === this.humanPlayerId;
    const msg = result.isDraw ? '🤝 ¡Empate!' : isWin ? '🏆 ¡Victoria!' : '😔 Derrota';
    Toast.show(this, msg);

    this.time.delayedCall(2000, () => {
      this.cleanupEvents();
      this.scene.start('ResultScene', result);
    });
  }

  /** Highlight which tiles the player can play */
  private updateHandHighlights() {
    if (!this.gameManager) return;
    const { left, right } = this.gameManager.getBoardEnds();
    this.playerHand.highlightPlayable(left, right);
    this.playerHand.enableInteraction();
    this.hud.updateTurn('Jugador', true);
  }

  shutdown() {
    this.cleanupEvents();
  }
}
